import { z } from 'zod';
// Signal API DTOs (snake_case wire contract). A Signal is a timestamped,
// evidence-carrying GTM event on an account — internal (analytics: a target
// account visited pricing, usage spiked) or external (research: funding, news,
// leadership). Signals persist append-only, render in the feed + per-account
// timeline, and can trigger actions. See
// packages/db/src/schema/signals.ts and the GTM Signals & Target Accounts PRD.
// The v1 signal taxonomy. Stored loosely as a varchar on the row (so recipes
// can grow without a migration); this const is the authoritative vocabulary the
// UI and recipes share.
export const SIGNAL_TYPES = [
    'account.visited',
    'account.high_intent_page',
    'account.return_visit',
    'account.usage_spike',
    'company.icp_fit_identified',
    'external.funding',
    'external.news',
    'external.leadership',
    'external.hiring',
    'external.product_launch',
];
// Where the signal came from: 'internal' = first-party analytics recipes;
// 'external' = Account Watch / research diffs (carry source URLs in payload).
export const SIGNAL_SOURCES = ['internal', 'external'];
export const signalSchema = z.object({
    id: z.string(),
    // The attribute company the signal is about; null when unresolved.
    company_id: z.string().nullable(),
    // The Target Account (company_profiles row, kind='account') this belongs to;
    // null for account-agnostic signals (e.g. a newly ICP-fit company).
    profile_id: z.string().nullable(),
    // One of SIGNAL_TYPES; kept as a string on the wire (see the varchar column).
    type: z.string(),
    source: z.enum(SIGNAL_SOURCES),
    // 1..3 — the strength/priority of the signal.
    strength: z.number().int().min(1).max(3),
    // Evidence: pages, counts, summary, source URLs, project — the context that
    // makes an alert actionable (never just a company name).
    payload: z.record(z.string(), z.unknown()),
    occurred_at: z.string(),
    created_at: z.string(),
});
// Feed + per-account timeline response. `next_cursor` is an opaque keyset
// cursor (`"<occurredAtIso>|<id>"`); null when there is no further page.
export const signalsListResponseSchema = z.object({
    signals: z.array(signalSchema),
    next_cursor: z.string().nullable(),
});
// ── Human labels (the ONE vocabulary for every surface) ──────────────────────
// UI feed/timeline, the configure dialog, AND backend deliveries (Slack/email)
// all read these, so the same signal never has two names. Unknown types fall
// back to a humanized key so an unmapped recipe still reads sensibly.
export const SIGNAL_TYPE_LABELS = {
    'account.visited': 'Target Account Visited',
    'account.high_intent_page': 'High-Intent Page Visit',
    'account.return_visit': 'Return Visit',
    'account.usage_spike': 'Usage Spike',
    'company.icp_fit_identified': 'New ICP-Fit Company',
    'external.funding': 'Funding',
    'external.news': 'News',
    'external.leadership': 'Leadership Change',
    'external.hiring': 'Hiring',
    'external.product_launch': 'Product Launch',
};
// Humanize an unmapped type: drop the namespace, split on `_`, Title Case
// ("external.product_launch" → "Product Launch").
function humanizeSignalType(type) {
    const leaf = type.includes('.')
        ? type.slice(type.lastIndexOf('.') + 1)
        : type;
    return leaf
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
export function signalTypeLabel(type) {
    return SIGNAL_TYPE_LABELS[type] ?? humanizeSignalType(type);
}
//# sourceMappingURL=signals.js.map